import * as THREE from 'three';
import { peana } from '../prims.ts';
import { mulberry32, type Rng } from '../rand.ts';

export interface Model {
  /** Origin at the base center, +Y up, faces +Z. */
  root: THREE.Group;
  /** 12 Hz stop-motion step (boiling ribbons, flicker, idle anim). */
  tick(n: number): void;
  /** Approximate height in model units (a human hero is ~1.8). */
  height: number;
  /** Humanoids only. */
  rig?: HeroRig;
  /**
   * Any other rigged model: its bones (pivot groups) in index order with their names. Bone 0 is usually the
   * inner group `g`. See `joint()` and anim/rigs.ts.
   */
  skel?: { bones: THREE.Object3D[]; names: string[] };
}

/** Pivot groups so the game can pose limbs at 12 fps. */
export interface HeroRig {
  body: THREE.Group;
  armL: THREE.Group;
  armR: THREE.Group;
  legL: THREE.Group;
  legR: THREE.Group;
  head: THREE.Group;
  weapon: THREE.Group;
  offhand: THREE.Group;
  /** Whole-figure pivot at the feet (used by the 'down' pose to lay the figure on its back). */
  figure: THREE.Group;
  /** Elbows and knees (figure() rigs), and the cape at the shoulders. */
  foreL?: THREE.Group;
  foreR?: THREE.Group;
  shinL?: THREE.Group;
  shinR?: THREE.Group;
  cape?: THREE.Group;
}

export type Pose = 'idle' | 'walkA' | 'walkB' | 'attackA' | 'attackB' | 'cast' | 'down' | 'work' | 'block' | 'bless' | 'raise';

export interface BaseOpts {
  /** Add the black display base (peana). Default false. */
  base?: boolean;
  /** Seed for random details (pebbles, jitter, flicker). */
  seed?: number;
}

/**
 * Lights a model would like to have. They are positioned in root space but NOT parented:
 * the caller decides whether to add them (`root.add(...root.userData.lights)`).
 * Intensities are physical (r155+) with decay 0, already compensated (legacy × LIGHT_K).
 */
export type ModelLights = THREE.PointLight[];

export interface Ctx {
  root: THREE.Group;
  /** Inner group: whole-model idle animation (bob, squash) goes here, never on root. */
  g: THREE.Group;
  rng: Rng;
  /** Centered noise in [-0.5, 0.5). */
  R: () => number;
  lights: ModelLights;
}

export const begin = (o: BaseOpts | undefined, baseR = 0.62, defSeed = 1): Ctx => {
  const rng = mulberry32(o?.seed ?? defSeed);
  const root = new THREE.Group();
  const g = new THREE.Group();
  g.name = 'model';
  root.add(g);
  if (o?.base) root.add(peana(baseR, mulberry32((o.seed ?? defSeed) + 7919)));
  const lights: ModelLights = [];
  root.userData.lights = lights;
  return { root, g, rng, R: () => rng() - 0.5, lights };
};

/**
 * Converts reference (r128, legacy, sRGB-space mixing) point-light intensities to r155+ physical units:
 * ×π for the legacy factor and ×3 so the pool of light reads as strongly once blended in linear space.
 */
export const LIGHT_K = Math.PI * 3;

/** Registers a point light. `legacy` is the reference intensity; the hex color keeps its reference (sRGB-space) tint. */
export const pointLight = (c: Ctx, color: number, legacy: number, distance: number, pos: THREE.Vector3): THREE.PointLight => {
  const col = new THREE.Color().setRGB(((color >> 16) & 255) / 255, ((color >> 8) & 255) / 255, (color & 255) / 255, THREE.LinearSRGBColorSpace);
  const L = new THREE.PointLight(col, legacy * LIGHT_K, distance, 0);
  L.position.copy(pos);
  L.userData.base = legacy * LIGHT_K;
  c.lights.push(L);
  return L;
};

export const done = (c: Ctx, height: number, tick: (n: number) => void = () => undefined, rig?: HeroRig, skel?: Model['skel']): Model => {
  const h = Math.round(height * 100) / 100;
  const m: Model = rig ? { root: c.root, tick, height: h, rig } : { root: c.root, tick, height: h };
  if (skel) m.skel = skel;
  return m;
};

/**
 * A bone: a pivot group at `at` (in the parent frame's coordinates) with an inner frame offset back, so the
 * builder keeps placing parts with the same absolute coordinates under `abs` and they follow the pivot.
 * `parentAbs` is the parent bone's `abs` (or the model group `g`).
 */
export const joint = (parentAbs: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = new THREE.Group();
  p.name = name;
  p.position.copy(at);
  parentAbs.add(p);
  const abs = new THREE.Group();
  abs.position.set(-at.x, -at.y, -at.z);
  p.add(abs);
  return { p, abs };
};

/** Declares a model's skeleton: `g` as bone 'root' first, then the named pivots in order. */
export const bonesOf = (c: Ctx, bones: Record<string, THREE.Object3D>): { bones: THREE.Object3D[]; names: string[] } => {
  const names = ['root', ...Object.keys(bones)];
  return { bones: [c.g, ...Object.values(bones)], names };
};

/** Tags a sub-assembly that the game animates on its own (turret, arm, bell, portcullis, crystal). */
export const part = (g: THREE.Group, name: string): THREE.Group => {
  g.userData.part = name;
  g.name = name;
  return g;
};
